import dayjs from 'dayjs';
import * as BulgarianLocale from 'dayjs/locale/bg';
import * as CatalanLocale from 'dayjs/locale/ca';
import * as GermanLocale from 'dayjs/locale/de';
import * as GreeceLocale from 'dayjs/locale/el';
import * as AmericanLocale from 'dayjs/locale/en';
import * as EnglishLocale from 'dayjs/locale/en-gb';
import * as SpanishLocale from 'dayjs/locale/es';
import * as FinnishLocale from 'dayjs/locale/fi';
import * as FrenchLocale from 'dayjs/locale/fr';
import * as ItalianLocale from 'dayjs/locale/it';
import * as DutchLocale from 'dayjs/locale/nl';
import * as PolishLocale from 'dayjs/locale/pl';
import * as PortugueseLocale from 'dayjs/locale/pt';
import * as BrasilLocale from 'dayjs/locale/pt-br';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { TranslateFunction } from 'react-localize-redux';

dayjs.extend(weekOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(calendar);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

type DateParamType = string | number | Date | dayjs.Dayjs;

const simpleTime = 'h:mma';
const standardDateTime = 'YYYY-MM-DD HH:mm';

export const dateTimeFormats = {
  simpleTime,
  standardDateTime,
};

export const msToIanaTimezoneName = (tzName: string): string => {
  const timezoneTable = {
    'A.U.S. Central Standard Time': 'Australia/Darwin',
    'A.U.S. Eastern Standard Time': 'Australia/Sydney',
    'Alaskan Standard Time': 'US/Alaska',
    'Arab Standard Time': 'Asia/Kuwait',
    'Arabian Standard Time': 'Asia/Muscat',
    'Arabic Standard Time': 'Asia/Baghdad',
    'Atlantic Standard Time': 'Canada/Atlantic',
    'Azores Standard Time': 'Atlantic/Azores',
    'Canada Central Standard Time': 'Canada/Central',
    'Cape Verde Standard Time': 'Atlantic/Cape_Verde',
    'Caucasus Standard Time': 'Asia/Baku',
    'Cen. Australia Standard Time': 'Australia/Adelaide',
    'Central America Standard Time': 'America/Guatemala',
    'Central Asia Standard Time': 'Asia/Dhaka',
    'Central Europe Standard Time': 'Europe/Belgrade',
    'Central European Standard Time': 'Europe/Sarajevo',
    'Central Pacific Standard Time': 'Asia/Magadan',
    'Central Standard Time': 'US/Central',
    'China Standard Time': 'Asia/Hong_Kong',
    'E. Africa Standard Time': 'Africa/Nairobi',
    'E. Australia Standard Time': 'Australia/Brisbane',
    'E. Europe Standard Time': 'Europe/Bucharest',
    'E. South America Standard Time': 'Brazil/East',
    'Eastern Standard Time': 'US/Eastern',
    'Egypt Standard Time': 'Africa/Cairo',
    'Ekaterinburg Standard Time': 'Asia/Yekaterinburg',
    'Fiji Islands Standard Time': 'Pacific/Fiji',
    'FLE Standard Time': 'Europe/Helsinki',
    'GMT Standard Time': 'Europe/Dublin',
    'Greenland Standard Time': 'America/Buenos_Aires',
    'Greenwich Standard Time': 'Europe/Dublin',
    'GTB Standard Time': 'Europe/Athens',
    'Hawaiian Standard Time': 'US/Hawaii',
    'India Standard Time': 'Asia/Kolkata',
    'Iran Standard Time': 'Asia/Tehran',
    'Israel Standard Time': 'Israel',
    'Korea Standard Time': 'Asia/Seoul',
    'Mexico Standard Time 2': 'America/Chihuahua',
    'Mexico Standard Time': 'America/Mexico_City',
    'Mountain Standard Time': 'US/Mountain',
    'Myanmar Standard Time': 'Asia/Yangon',
    'N. Central Asia Standard Time': 'Asia/Almaty',
    'Nepal Standard Time': 'Asia/Kathmandu',
    'New Zealand Standard Time': 'Pacific/Auckland',
    'Newfoundland and Labrador Standard Time': 'Canada/Newfoundland',
    'North Asia East Standard Time': 'Asia/Irkutsk',
    'North Asia Standard Time': 'Asia/Krasnoyarsk',
    'Pacific S.A. Standard Time': 'America/Santiago',
    'Pacific Standard Time': 'US/Pacific',
    'Romance Standard Time': 'Europe/Paris',
    'Russian Standard Time': 'Europe/Moscow',
    'S.A. Eastern Standard Time': 'America/Buenos_Aires',
    'S.A. Pacific Standard Time': 'America/Lima',
    'S.A. Western Standard Time': 'America/Caracas',
    'Samoa Standard Time': 'Pacific/Samoa',
    'SE Asia Standard Time': 'Asia/Bangkok',
    'Singapore Standard Time': 'Asia/Singapore',
    'South Africa Standard Time': 'Africa/Johannesburg',
    'Sri Lanka Standard Time': 'Asia/Colombo',
    'Taipei Standard Time': 'Asia/Taipei',
    'Tasmania Standard Time': 'Australia/Hobart',
    'Tokyo Standard Time': 'Asia/Tokyo',
    'Tonga Standard Time': 'Pacific/Tongatapu',
    'Transitional Islamic State of Afghanistan Standard Time': 'Asia/Kabul',
    'U.S. Eastern Standard Time': 'US/Eastern',
    'U.S. Mountain Standard Time': 'US/Mountain',
    'Vladivostok Standard Time': 'Asia/Vladivostok',
    'W. Australia Standard Time': 'Australia/Perth',
    'W. Central Africa Standard Time': 'Africa/Luanda',
    'W. Europe Standard Time': 'Europe/Amsterdam',
    'West Asia Standard Time': 'Asia/Karachi',
    'West Pacific Standard Time': 'Pacific/Guam',
    'Yakutsk Standard Time': 'Asia/Yakutsk',
  };

  return timezoneTable[tzName];
};

export const formatDateTimeString = (
  dateTime: DateParamType,
  timeFormat = simpleTime,
): string => dayjs(dateTime).format(timeFormat);

export const formatDate = (dateTime: string, format: string): Date =>
  dayjs(dateTime, format).toDate();

export const addTime = (
  dateTime: DateParamType,
  unit: dayjs.ManipulateType,
  value: number,
): Date => dayjs(dateTime).add(value, unit).toDate();

export const getDateFromMsDate = (msDate: string): Date => {
  const timeStamp = Number(msDate.slice(6, -5));
  return dayjs.unix(timeStamp).toDate();
};

export const getLocale = (lang: string): ILocale => {
  const locale = {
    bg: BulgarianLocale,
    'ca-ES': CatalanLocale,
    de: GermanLocale,
    el: GreeceLocale,
    en: EnglishLocale,
    'en-US': AmericanLocale,
    es: SpanishLocale,
    'es-MX': SpanishLocale,
    fi: FinnishLocale,
    fr: FrenchLocale,
    it: ItalianLocale,
    nl: DutchLocale,
    pl: PolishLocale,
    pt: PortugueseLocale,
    'pt-BR': BrasilLocale,
  };
  return locale[lang];
};

export const getRelativeDate = (
  dateParam: DateParamType,
  translate: TranslateFunction,
  lang = 'en',
): string => {
  const date = dayjs(dateParam).locale(getLocale(lang)?.name || 'en-gb');
  const time = date.format('HH:mm');
  const currentWeek = dayjs()
    .locale(getLocale(lang)?.name || 'en-gb')
    .week();

  if (date.isToday()) {
    return translate('RelativeDate_SameDay', {
      time,
    }) as string;
  }

  if (date.isYesterday()) {
    return translate('RelativeDate_Yesterday', {
      time,
    }) as string;
  }

  // is a day of the current week
  if (date.week() === currentWeek) {
    const day = date.format('dddd');
    return translate('RelativeDate_LastDay', {
      day,
      time,
    }) as string;
  }

  // is a day of the previous week
  if (currentWeek - date.week() === 1) {
    const day = date.format('dddd');
    return translate('RelativeDate_LastWeek', {
      day,
      time,
    }) as string;
  }

  return date.format('L').toString();
};

export const setDate = (
  date: DateParamType,
  value: number,
  unit: dayjs.UnitType = 'day',
): Date => dayjs(date).set(unit, value).toDate();

export const toSeconds = (
  hours: number,
  minutes: number,
  seconds: number,
): number => hours * 3600 + minutes * 60 + seconds;

export const isWithinInterval = (
  date: DateParamType,
  startTime: string | number | dayjs.Dayjs | Date,
  endTime: string | number | dayjs.Dayjs | Date,
  precisionUnit: dayjs.OpUnitType,
): boolean => dayjs(date).isBetween(startTime, endTime, precisionUnit, '[]');

export const utcToZoned = (date: DateParamType, timeZone: string): Date => {
  const zonedString = dayjs(date).toDate().toLocaleString('en', { timeZone });
  return dayjs(zonedString).toDate();
};

export const zonedToUtc = (date: DateParamType, timeZone: string): Date => {
  const utcDate = dayjs(date);
  const zonedDate = utcToZoned(date, timeZone);
  const diff = utcDate.diff(zonedDate, 'hour');
  return utcDate.add(diff, 'hour').toDate();
};
